import { useEffect, useState } from "react";
import { Plus, Trash2, Save, GripVertical } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const blank = () => ({
  id: "", name: "", price_inr: 0, yearly_inr: 0, words_limit: 10000, period_days: 30,
  features: [], active: true, popular: false, cta_label: "Get plan", display_order: 99,
});

function PlanCard({ plan, onSave, onDelete }) {
  const [edit, setEdit] = useState(plan);
  const [feat, setFeat] = useState((plan.features || []).join("\n"));
  const [busy, setBusy] = useState(false);
  const planKey = plan.id + "::" + (plan.features || []).join("|") + "::" + plan.name + plan.price_inr + plan.yearly_inr;
  const [seen, setSeen] = useState(planKey);
  if (seen !== planKey) {
    setSeen(planKey);
    setEdit(plan);
    setFeat((plan.features || []).join("\n"));
  }

  const save = async () => {
    setBusy(true);
    try {
      await onSave({ ...edit, features: feat.split("\n").map((f) => f.trim()).filter(Boolean) });
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-brand-borderc p-5 space-y-3" data-testid={`plan-card-${plan.id}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2"><GripVertical className="w-4 h-4 text-brand-muted" /><div className="font-bold text-brand-navy">{plan.id}</div></div>
        <div className="flex items-center gap-2 text-xs">
          <label className="flex items-center gap-1"><input type="checkbox" checked={!!edit.active} onChange={(e) => setEdit({ ...edit, active: e.target.checked })} /> Active</label>
          <label className="flex items-center gap-1"><input type="checkbox" checked={!!edit.popular} onChange={(e) => setEdit({ ...edit, popular: e.target.checked })} /> Popular</label>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div><label className="text-xs text-brand-muted">Name</label><input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className="w-full px-3 py-2 rounded-xl border border-brand-borderc" /></div>
        <div><label className="text-xs text-brand-muted">CTA label</label><input value={edit.cta_label || ""} onChange={(e) => setEdit({ ...edit, cta_label: e.target.value })} className="w-full px-3 py-2 rounded-xl border border-brand-borderc" /></div>
        <div><label className="text-xs text-brand-muted">Monthly ₹</label><input type="number" value={edit.price_inr} onChange={(e) => setEdit({ ...edit, price_inr: +e.target.value })} className="w-full px-3 py-2 rounded-xl border border-brand-borderc" /></div>
        <div><label className="text-xs text-brand-muted">Yearly ₹</label><input type="number" value={edit.yearly_inr} onChange={(e) => setEdit({ ...edit, yearly_inr: +e.target.value })} className="w-full px-3 py-2 rounded-xl border border-brand-borderc" /></div>
        <div><label className="text-xs text-brand-muted">Words limit</label><input type="number" value={edit.words_limit} onChange={(e) => setEdit({ ...edit, words_limit: +e.target.value })} className="w-full px-3 py-2 rounded-xl border border-brand-borderc" /></div>
        <div><label className="text-xs text-brand-muted">Period (days)</label><input type="number" value={edit.period_days} onChange={(e) => setEdit({ ...edit, period_days: +e.target.value })} className="w-full px-3 py-2 rounded-xl border border-brand-borderc" /></div>
        <div className="col-span-2"><label className="text-xs text-brand-muted">Display order</label><input type="number" value={edit.display_order} onChange={(e) => setEdit({ ...edit, display_order: +e.target.value })} className="w-full px-3 py-2 rounded-xl border border-brand-borderc" /></div>
      </div>
      <div>
        <label className="text-xs text-brand-muted">Features (one per line)</label>
        <textarea value={feat} onChange={(e) => setFeat(e.target.value)} rows={4} className="w-full px-3 py-2 rounded-xl border border-brand-borderc text-sm font-mono" />
      </div>
      <div className="flex items-center justify-between pt-1">
        <button data-testid={`save-plan-${plan.id}`} disabled={busy} onClick={save} className="btn-primary-gradient rounded-full px-4 py-2 text-xs font-semibold inline-flex items-center gap-1"><Save className="w-3.5 h-3.5" />{busy ? "Saving…" : "Save"}</button>
        {plan.id !== "free" && plan.id && <button onClick={() => onDelete(plan.id)} className="text-red-500 text-xs inline-flex items-center gap-1 hover:underline"><Trash2 className="w-3.5 h-3.5" />Delete</button>}
      </div>
    </div>
  );
}

export default function AdminPlans() {
  const [plans, setPlans] = useState([]);
  const [newPlan, setNewPlan] = useState(blank());

  const load = async () => { const { data } = await api.get("/admin/plans"); setPlans(data); };
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await api.get("/admin/plans");
      if (!cancelled) setPlans(data);
    })();
    return () => { cancelled = true; };
  }, []);

  const save = async (p) => {
    if (!p.id || !p.name) { toast.error("ID and name are required"); return; }
    try { await api.put(`/admin/plans/${p.id}`, p); toast.success(`${p.name} saved`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm(`Delete plan "${id}"? Existing users on this plan will keep their access until expiry.`)) return;
    try { await api.delete(`/admin/plans/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const addNew = async () => {
    if (!newPlan.id.match(/^[a-z][a-z0-9_-]{1,30}$/)) { toast.error("Use a lowercase id like 'team-pro'."); return; }
    await save(newPlan);
    setNewPlan(blank());
  };

  return (
    <div className="px-5 lg:px-10 py-8 max-w-6xl mx-auto" data-testid="admin-plans">
      <h1 className="text-3xl font-bold text-brand-navy">Pricing & Plans</h1>
      <p className="text-sm text-brand-muted mt-1">Edit plan names, prices, word limits, and features. Changes go live instantly across the marketing site and Billing page.</p>

      <div className="mt-6 grid md:grid-cols-2 gap-5">
        {plans.map((p) => <PlanCard key={p.id} plan={p} onSave={save} onDelete={remove} />)}
      </div>

      <div className="mt-8">
        <h2 className="text-xl font-semibold text-brand-navy">Add a new plan</h2>
        <div className="mt-3 bg-white rounded-2xl border border-brand-borderc p-5 grid sm:grid-cols-3 gap-3 text-sm">
          <input placeholder="id (lowercase, e.g. studio)" value={newPlan.id} onChange={(e) => setNewPlan({ ...newPlan, id: e.target.value.toLowerCase() })} className="px-3 py-2 rounded-xl border border-brand-borderc" />
          <input placeholder="Name" value={newPlan.name} onChange={(e) => setNewPlan({ ...newPlan, name: e.target.value })} className="px-3 py-2 rounded-xl border border-brand-borderc" />
          <input placeholder="Monthly ₹" type="number" value={newPlan.price_inr} onChange={(e) => setNewPlan({ ...newPlan, price_inr: +e.target.value })} className="px-3 py-2 rounded-xl border border-brand-borderc" />
          <button onClick={addNew} className="btn-primary-gradient rounded-full px-4 py-2 font-semibold inline-flex items-center justify-center gap-1 sm:col-span-3"><Plus className="w-4 h-4" />Create plan</button>
        </div>
      </div>
    </div>
  );
}
